import { BaseElement, define, isBrowser, prefersReducedMotion } from './base';
import { supportsPopover } from './position';
import { t } from './strings';

export type ToastVariant = 'sakura' | 'info' | 'success' | 'warning' | 'danger';
export type ToastPosition = 'top-start' | 'top-center' | 'top-end' | 'bottom-start' | 'bottom-center' | 'bottom-end';

export interface ToastAction {
  label: string;
  onClick?: (toast: ToastHandle) => void;
}

export interface ToastOptions {
  title?: string;
  message?: string | Node;
  variant?: ToastVariant;
  /** Durée d'affichage en ms ; 0 = reste affiché jusqu'à fermeture. Défaut 5000. */
  duration?: number;
  closable?: boolean;
  action?: ToastAction;
  position?: ToastPosition;
}

export interface ToastHandle {
  id: string;
  element: HTMLElement;
  dismiss: () => void;
}

const MAX_VISIBLE = 5;
let counter = 0;
const regions = new Map<ToastPosition, HTMLElement>();
const live = new Map<string, ToastHandle>();
let defaultPosition: ToastPosition = 'bottom-end';

/** Conteneur des toasts (une région aria-live par position). */
export class HnToastRegion extends BaseElement {
  static readonly tagName = 'hn-toast-region';
  connectedCallback(): void {
    this.setAttribute('role', 'region');
    this.setAttribute('aria-label', t('notifications'));
    this.setAttribute('aria-live', 'polite');
    this.setAttribute('aria-relevant', 'additions');
  }
}
define(HnToastRegion.tagName, HnToastRegion);

/** Change la position par défaut des toasts. */
export function setToastPosition(position: ToastPosition): void {
  defaultPosition = position;
}

function getRegion(position: ToastPosition): { region: HTMLElement; created: boolean } {
  let region = regions.get(position);
  let created = false;
  if (!region || !region.isConnected) {
    region = document.createElement(HnToastRegion.tagName);
    region.dataset.position = position;
    if (supportsPopover) region.setAttribute('popover', 'manual');
    document.body.append(region);
    regions.set(position, region);
    created = true;
  }
  if (supportsPopover && !region.matches(':popover-open')) region.showPopover();
  return { region, created };
}

/**
 * Remet les toasts au premier plan (au-dessus d'une modale ouverte ensuite).
 * Appelé automatiquement par <hn-dialog>.
 */
export function bringToastsToFront(): void {
  if (!supportsPopover) return;
  for (const region of regions.values()) {
    if (region.isConnected && region.childElementCount && region.matches(':popover-open')) {
      region.hidePopover();
      region.showPopover();
    }
  }
}

function buildToast(id: string, options: ToastOptions, handle: () => ToastHandle): HTMLElement {
  const variant = options.variant ?? 'sakura';
  const el = document.createElement('div');
  el.id = id;
  el.className = `hn-toast${variant === 'sakura' ? '' : ` hn-toast--${variant}`}`;
  el.dataset.variant = variant;

  const icon = document.createElement('span');
  icon.className = 'hn-toast__icon';
  icon.setAttribute('aria-hidden', 'true');

  const content = document.createElement('div');
  content.className = 'hn-toast__content';
  if (options.title) {
    const title = document.createElement('p');
    title.className = 'hn-toast__title';
    title.textContent = options.title;
    content.append(title);
  }
  if (options.message) {
    const message = document.createElement('p');
    message.className = 'hn-toast__message';
    if (typeof options.message === 'string') message.textContent = options.message;
    else message.append(options.message);
    content.append(message);
  }

  const actions = document.createElement('div');
  actions.className = 'hn-toast__actions';
  if (options.action) {
    const button = document.createElement('button');
    button.type = 'button';
    button.className = 'hn-btn hn-btn--soft hn-btn--xs';
    button.textContent = options.action.label;
    button.addEventListener('click', () => {
      options.action?.onClick?.(handle());
      handle().dismiss();
    });
    actions.append(button);
  }
  if (options.closable !== false) {
    const close = document.createElement('button');
    close.type = 'button';
    close.className = 'hn-close';
    close.setAttribute('aria-label', t('dismiss'));
    close.addEventListener('click', () => handle().dismiss());
    actions.append(close);
  }

  el.append(icon, content, actions);
  return el;
}

/**
 * Affiche une notification éphémère.
 *   toast('Enregistré')
 *   toast({ title: 'Mankai !', message: 'Pleine floraison à Kyoto', variant: 'success' })
 */
export function toast(input: string | ToastOptions): ToastHandle {
  const options: ToastOptions = typeof input === 'string' ? { message: input } : input;
  const id = `hn-toast-${++counter}`;
  if (!isBrowser) return { id, element: null as unknown as HTMLElement, dismiss: () => undefined };

  const duration = options.duration ?? 5000;
  let remaining = duration;
  let startedAt = 0;
  let timer: ReturnType<typeof setTimeout> | undefined;
  let dismissed = false;

  const handle: ToastHandle = {
    id,
    element: null as unknown as HTMLElement,
    dismiss: () => {
      if (dismissed) return;
      dismissed = true;
      clearTimeout(timer);
      const el = handle.element;
      live.delete(id);
      const remove = () => {
        const region = el.parentElement;
        el.remove();
        if (region && !region.childElementCount && supportsPopover && region.matches(':popover-open')) region.hidePopover();
      };
      if (prefersReducedMotion()) {
        remove();
        return;
      }
      el.dataset.state = 'closing';
      el.addEventListener('animationend', remove, { once: true });
      setTimeout(remove, 400);
    },
  };

  const el = buildToast(id, options, () => handle);
  handle.element = el;

  if (duration > 0) {
    el.style.setProperty('--hn-toast-duration', `${duration}ms`);
    const timerBar = document.createElement('span');
    timerBar.className = 'hn-toast__timer';
    timerBar.setAttribute('aria-hidden', 'true');
    el.append(timerBar);
    const start = () => {
      startedAt = Date.now();
      clearTimeout(timer);
      timer = setTimeout(handle.dismiss, remaining);
    };
    const pause = () => {
      clearTimeout(timer);
      remaining -= Date.now() - startedAt;
    };
    el.addEventListener('pointerenter', pause);
    el.addEventListener('pointerleave', start);
    el.addEventListener('focusin', pause);
    el.addEventListener('focusout', event => {
      if (!el.contains(event.relatedTarget as Node)) start();
    });
    start();
  }

  live.set(id, handle);
  const { region, created } = getRegion(options.position ?? defaultPosition);
  const insert = () => {
    region.append(el);
    const items = region.querySelectorAll('.hn-toast:not([data-state="closing"])');
    if (items.length > MAX_VISIBLE) live.get(items[0].id)?.dismiss();
  };
  // Une région aria-live doit exister avant son contenu pour que l'annonce soit lue.
  if (created) requestAnimationFrame(insert);
  else insert();
  return handle;
}

toast.success = (message: string, options: Omit<ToastOptions, 'message' | 'variant'> = {}) => toast({ ...options, message, variant: 'success' });
toast.error = (message: string, options: Omit<ToastOptions, 'message' | 'variant'> = {}) => toast({ ...options, message, variant: 'danger' });
toast.warning = (message: string, options: Omit<ToastOptions, 'message' | 'variant'> = {}) => toast({ ...options, message, variant: 'warning' });
toast.info = (message: string, options: Omit<ToastOptions, 'message' | 'variant'> = {}) => toast({ ...options, message, variant: 'info' });

/** Ferme toutes les notifications affichées. */
export function dismissAllToasts(): void {
  live.clear();
  for (const region of regions.values()) {
    region.querySelectorAll<HTMLElement>('.hn-toast').forEach(el => el.remove());
    if (supportsPopover && region.matches(':popover-open')) region.hidePopover();
  }
}
